/**
 * Admin Bexio transparency page (B1b, read-only).
 *
 * Shows a read-only mirror of Bexio data (status, sync runs, invoices, contacts)
 * from the `bexio_*` mirror tables. This data is NOT mixed with the internal
 * "Belege" store (`/admin/belege`) and does not create Vantio finance documents.
 *
 * The manual-sync button is admin-only and calls the admin JWT route
 * `POST /api/admin/bexio/sync` (never the cron secret route). No mapping, no
 * write-back, no KPI integration.
 */
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

import Badge from "../../components/admin/bexio/mapping/BexioBadge";
import BexioContactMappingModal from "../../components/admin/bexio/mapping/BexioContactMappingModal";
import BexioInvoiceMappingModal from "../../components/admin/bexio/mapping/BexioInvoiceMappingModal";

import {
  CONNECTION_STATUS_LABEL,
  INVOICE_STATUS_LABEL,
  MAPPING_STATUS_LABEL,
  SCOPE_LABEL,
  SYNC_STATUS_LABEL,
  connectionTone,
  INVOICE_MAPPING_STATUS_LABEL,
  fmtDateCH,
  fmtInvoiceMoney,
  invoiceMappingTone,
  tenancyPrimaryName,
  mapLabel,
  mappingTone,
  syncStatusTone,
} from "../../components/admin/bexio/bexioFormat";
import { useBexioSync } from "../../components/admin/bexio/useBexioSync";

import {
  fetchBexioStatus,
  fetchBexioReconciliation,
  fetchBexioExpectedVsInvoiced,
  fetchBexioInvoiceDraftPreview,
  fetchBexioSyncRuns,
  fetchBexioInvoices,
  fetchBexioInvoicePositions,
  fetchBexioContacts,
  getBexioContactMappingStatuses,
  getBexioContactMappingSuggestions,
  confirmBexioContactMapping,
  changeBexioContactMapping,
  deleteBexioContactMapping,
  fetchBexioInvoiceMapping,
  fetchBexioInvoiceMappingStatuses,
  createBexioInvoiceMapping,
  updateBexioInvoiceMapping,
  deleteBexioInvoiceMapping,
} from "../../api/adminData/bexio";
import { fetchAdminTenanciesAll } from "../../api/adminData/tenancies";
import { getTokenPayload } from "../../authStore";
import BexioReconciliationSection from "./components/BexioReconciliationSection";
import BexioExpectedVsInvoicedSection from "./components/BexioExpectedVsInvoicedSection";
import BexioInvoiceDraftPreviewSection from "./components/BexioInvoiceDraftPreviewSection";

const PAGE_SIZE = 25;

// Invoices use a DEDICATED page size — never the shared PAGE_SIZE (which also
// drives the sync-runs and contacts fetches). The invoice list auto-loads ALL
// pages on open (offset loop), so this is just the per-request batch size.
const INVOICE_PAGE_SIZE = 50;
// Hard fail-safe against an endless loop: never issue more than this many invoice
// page requests, even if the server `total` and the delivered items diverge
// (e.g. an invoice deleted in Bexio between two fetches, or an off-by-one).
// 40 pages × 50 = 2000 invoices — far above any realistic org, but bounded.
const INVOICE_MAX_PAGES = 40;

function isAdmin() {
  try {
    return getTokenPayload()?.role === "admin";
  } catch {
    return false;
  }
}

function fmtDateTime(v) {
  if (!v) return "—";
  try {
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString("de-CH", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
}


function mappingActionLabel(status) {
  if (status === "mapped" || status === "invalid") return "Verwalten";
  if (status === "suggested" || status === "conflict" || status === "not_contact_level_mappable")
    return "Vorschlag prüfen";
  return "Zuordnen";
}




function StatCard({ label, value, hint }) {
  return (
    <div className="rounded-[10px] border border-border bg-card p-4">
      <div className="text-[10px] font-medium uppercase tracking-[0.09em] text-muted-foreground">
        {label}
      </div>
      <div className="mt-1 text-[20px] font-bold text-foreground">{value}</div>
      {hint ? <div className="mt-1 text-[11px] text-muted-foreground">{hint}</div> : null}
    </div>
  );
}

export default function AdminBexioPage() {
  const [status, setStatus] = useState(null);
  const [syncRuns, setSyncRuns] = useState([]);
  const [invoices, setInvoices] = useState([]);
  // Auto-load-all invoice pagination state (B1c invoice-mapping reachability fix).
  const [invoiceTotal, setInvoiceTotal] = useState(0);
  const [isLoadingInvoices, setIsLoadingInvoices] = useState(false);
  const [invoiceLoadError, setInvoiceLoadError] = useState("");
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // B1c.3.1b: reconciliation dashboard — DECOUPLED from loadAll so a failure here
  // never kills the rest of the page.
  const [reconciliation, setReconciliation] = useState(null);
  const [reconciliationLoading, setReconciliationLoading] = useState(true);
  const [reconciliationError, setReconciliationError] = useState("");
  // B1c.3.2d: expected-vs-invoiced side-by-side — DECOUPLED from loadAll and from
  // the reconciliation fetch; own state + own month selector.
  const _now = new Date();
  const [expVsInv, setExpVsInv] = useState(null);
  const [expVsInvLoading, setExpVsInvLoading] = useState(true);
  const [expVsInvError, setExpVsInvError] = useState("");
  const [expVsInvYear, setExpVsInvYear] = useState(_now.getFullYear());
  const [expVsInvMonth, setExpVsInvMonth] = useState(_now.getMonth() + 1);
  // Slice 1a: read-only, Bexio-write-free invoice-draft preview — DECOUPLED fetch,
  // own month selector. Never triggers a Bexio write.
  const [draftPreview, setDraftPreview] = useState(null);
  const [draftPreviewLoading, setDraftPreviewLoading] = useState(true);
  const [draftPreviewError, setDraftPreviewError] = useState("");
  const [draftPreviewYear, setDraftPreviewYear] = useState(_now.getFullYear());
  const [draftPreviewMonth, setDraftPreviewMonth] = useState(_now.getMonth() + 1);
  // B1c-v1: bexio_contact_id -> mapping status object.
  const [mappingStatuses, setMappingStatuses] = useState({});
  const [modalContact, setModalContact] = useState(null);
  // B1c.2b: invoice row whose Bexio positions modal is open (read-only display).
  const [modalInvoice, setModalInvoice] = useState(null);
  // B1c.2d: local BexioInvoice.id -> invoice mapping status object.
  const [invoiceMappingStatuses, setInvoiceMappingStatuses] = useState({});
  // B1c.2d: invoice row whose mapping modal is open.
  const [mappingInvoice, setMappingInvoice] = useState(null);

  const admin = isAdmin();

  const loadInvoiceMappingStatuses = useCallback(async (invoiceItems) => {
    const ids = (invoiceItems || []).map((r) => r.id).filter((v) => v != null);
    if (!ids.length) {
      setInvoiceMappingStatuses({});
      return;
    }
    try {
      const res = await fetchBexioInvoiceMappingStatuses(ids);
      const map = {};
      (res?.items || []).forEach((it) => {
        map[it.invoice_id] = it;
      });
      setInvoiceMappingStatuses(map);
    } catch {
      // Non-fatal: the invoice table still renders without mapping badges.
    }
  }, []);

  const loadMappingStatuses = useCallback(async (contactItems) => {
    const ids = (contactItems || [])
      .map((c) => c.bexio_contact_id)
      .filter((v) => v != null);
    if (!ids.length) {
      setMappingStatuses({});
      return;
    }
    try {
      const res = await getBexioContactMappingStatuses(ids);
      const map = {};
      (res?.items || []).forEach((it) => {
        map[it.bexio_contact_id] = it;
      });
      setMappingStatuses(map);
    } catch {
      // Non-fatal: the contact table still renders without mapping data.
    }
  }, []);

  // Auto-load ALL invoice pages: page through offset 0, INVOICE_PAGE_SIZE, … until
  // we hold every mirrored invoice (invoices.length >= total). Progressive: each
  // page is appended to state so the "x von y" indicator updates live. Two hard
  // fail-safes prevent an endless loop if `total` and the delivered items diverge:
  //   (a) INVOICE_MAX_PAGES caps the number of requests (the for-loop bound);
  //   (b) an empty page stops immediately (never advance offset into the void).
  // Dedupe by id (fallback bexio_invoice_id) so an overlapping row across two
  // pages is never counted twice. Append order preserves the server's stable sort.
  const loadAllInvoices = useCallback(async () => {
    setIsLoadingInvoices(true);
    setInvoiceLoadError("");
    const acc = [];
    const seen = new Set();
    const keyOf = (r) =>
      r?.id != null
        ? `id:${r.id}`
        : r?.bexio_invoice_id != null
        ? `bx:${r.bexio_invoice_id}`
        : `idx:${acc.length}`;
    let total = 0;
    let offset = 0;
    for (let page = 0; page < INVOICE_MAX_PAGES; page++) {
      let resp;
      try {
        resp = await fetchBexioInvoices({
          limit: INVOICE_PAGE_SIZE,
          offset,
        });
      } catch (e) {
        // Partial failure: keep what already loaded, surface an inline error.
        setInvoiceLoadError(
          e?.message || "Weitere Bexio-Rechnungen konnten nicht geladen werden."
        );
        break;
      }
      const items = Array.isArray(resp?.items) ? resp.items : [];
      if (typeof resp?.total === "number") total = resp.total;
      for (const it of items) {
        const k = keyOf(it);
        if (seen.has(k)) continue;
        seen.add(k);
        acc.push(it);
      }
      setInvoices([...acc]);
      setInvoiceTotal(total);
      // Fail-safe (b): an empty page means there is nothing more to fetch.
      if (items.length === 0) break;
      // Advance by the ACTUAL count returned (robust vs. server page size).
      offset += items.length;
      // Done: we hold at least `total` rows.
      if (total > 0 && acc.length >= total) break;
      // Defensive last-page stop even if `total` is absent/wrong.
      if (items.length < INVOICE_PAGE_SIZE) break;
      // Fail-safe (a): the for-loop bound (INVOICE_MAX_PAGES) is the hard cap.
    }
    setIsLoadingInvoices(false);
    // Mapping badges over the FULL (or partial-on-error) set — one batched call.
    loadInvoiceMappingStatuses(acc);
  }, [loadInvoiceMappingStatuses]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      // Status / sync-runs / contacts gate the top spinner. Invoices auto-load
      // separately (below) with their own progress indicator so the "x von y"
      // count is visible live instead of blocking behind the spinner.
      const [st, runs, con] = await Promise.all([
        fetchBexioStatus(),
        fetchBexioSyncRuns({ limit: PAGE_SIZE, offset: 0 }),
        fetchBexioContacts({ limit: PAGE_SIZE, offset: 0 }),
      ]);
      setStatus(st);
      setSyncRuns(Array.isArray(runs?.items) ? runs.items : []);
      const conItems = Array.isArray(con?.items) ? con.items : [];
      setContacts(conItems);
      loadMappingStatuses(conItems);
    } catch (e) {
      setError(e?.message || "Bexio-Daten konnten nicht geladen werden.");
      setLoading(false);
      return;
    }
    setLoading(false);
    // Progressive auto-load of every invoice page (own indicator; not gated by
    // the top spinner). Own try/catch inside → never rejects loadAll.
    await loadAllInvoices();
  }, [loadMappingStatuses, loadAllInvoices]);

  // After a confirm/change/remove: refresh only the mapping statuses + close modal.
  const handleMappingChanged = useCallback(async () => {
    setModalContact(null);
    await loadMappingStatuses(contacts);
  }, [loadMappingStatuses, contacts]);

  // B1c.2d: after invoice mapping create/patch/delete, refresh invoice badges.
  const handleInvoiceMappingChanged = useCallback(async () => {
    await loadInvoiceMappingStatuses(invoices);
  }, [loadInvoiceMappingStatuses, invoices]);

  // B1c.3.1b: read-only reconciliation fetch. The ONLY backend call this makes is
  // GET /api/admin/bexio/reconciliation — never a mutation, never a sync. Isolated
  // try/catch so a reconciliation failure does not affect loadAll or the raw table.
  const loadReconciliation = useCallback(async () => {
    setReconciliationLoading(true);
    setReconciliationError("");
    try {
      const data = await fetchBexioReconciliation();
      setReconciliation(data);
    } catch (e) {
      setReconciliation(null);
      setReconciliationError(
        e?.message || "Zahlungsabgleich konnte nicht geladen werden."
      );
    } finally {
      setReconciliationLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
    loadReconciliation();
  }, [loadAll, loadReconciliation]);

  // B1c.3.2d: read-only side-by-side fetch. The ONLY backend call this makes is
  // GET /api/admin/bexio/expected-vs-invoiced — never a mutation, never a sync.
  // Isolated try/catch so a failure here does not affect the rest of the page.
  const loadExpVsInv = useCallback(async (year, month) => {
    setExpVsInvLoading(true);
    setExpVsInvError("");
    try {
      const data = await fetchBexioExpectedVsInvoiced({ year, month });
      setExpVsInv(data);
    } catch (e) {
      setExpVsInv(null);
      setExpVsInvError(e?.message || "Gegenüberstellung konnte nicht geladen werden.");
    } finally {
      setExpVsInvLoading(false);
    }
  }, []);

  // Refetch on mount and whenever the selected month/year changes.
  useEffect(() => {
    loadExpVsInv(expVsInvYear, expVsInvMonth);
  }, [loadExpVsInv, expVsInvYear, expVsInvMonth]);

  const handleExpVsInvMonthChange = useCallback((year, month) => {
    setExpVsInvYear(year);
    setExpVsInvMonth(month);
  }, []);

  // Slice 1a: read-only invoice-draft preview fetch. The ONLY backend call this
  // makes is GET /api/admin/bexio/invoice-draft-preview — never a mutation, never a
  // sync, never a Bexio draft. Isolated try/catch so a failure here does not affect
  // the rest of the page.
  const loadDraftPreview = useCallback(async (year, month) => {
    setDraftPreviewLoading(true);
    setDraftPreviewError("");
    try {
      const data = await fetchBexioInvoiceDraftPreview({ year, month });
      setDraftPreview(data);
    } catch (e) {
      setDraftPreview(null);
      setDraftPreviewError(
        e?.message || "Entwurf-Vorschau konnte nicht geladen werden."
      );
    } finally {
      setDraftPreviewLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDraftPreview(draftPreviewYear, draftPreviewMonth);
  }, [loadDraftPreview, draftPreviewYear, draftPreviewMonth]);

  const handleDraftPreviewMonthChange = useCallback((year, month) => {
    setDraftPreviewYear(year);
    setDraftPreviewMonth(month);
  }, []);

  // Shared with the overview page; one endpoint, one set of messages.
  const { syncing, sync: handleSync } = useBexioSync(loadAll);

  return (
    <div className="mx-auto max-w-[1400px] p-4 md:p-6">
      {/* The "Bexio" heading and its subline moved to BexioLayout, which draws
          them once for the whole workspace. Keeping them here as well printed
          the title twice on this page. The sync action deliberately did NOT
          move: it owns `syncing` and refetches through `loadAll`, so hoisting
          it into the shell would mean lifting this page's state across the
          `Outlet` boundary — a business-logic change this slice does not make. */}
      <header className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="m-0 max-w-[720px] text-[12px] text-muted-foreground">
            Schreibgeschützter Spiegel der Daten aus bexio. Diese Daten werden nicht mit der
            Belege-Sammelstelle vermischt und erzeugen keine Vantio-Belege.
          </p>
        </div>
        {admin ? (
          <button
            type="button"
            onClick={handleSync}
            disabled={syncing}
            className="rounded-[10px] bg-primary px-4 py-2 text-[13px] font-semibold text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {syncing ? "Synchronisiert …" : "Jetzt synchronisieren"}
          </button>
        ) : (
          <span className="text-[11px] text-muted-foreground">
            Nur Admins können synchronisieren.
          </span>
        )}
      </header>

      {error ? (
        <div className="mb-4 rounded-[10px] border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/10 p-4 text-[13px] text-red-700 dark:text-red-300">
          <div>{error}</div>
          <button
            type="button"
            onClick={loadAll}
            className="mt-2 rounded-[8px] border border-red-200 dark:border-red-500/30 px-3 py-1 text-[12px] text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-500/20"
          >
            Erneut versuchen
          </button>
        </div>
      ) : null}

      {loading ? (
        <div className="flex items-center gap-2 py-16 text-[13px] text-muted-foreground">
          <span className="h-4 w-4 animate-spin rounded-full border-2 border-input border-t-primary" />
          Lädt Bexio-Daten …
        </div>
      ) : (
        <>
          {/* Status cards */}
          <section className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-5">
            <div className="rounded-[10px] border border-border bg-card p-4">
              <div className="text-[10px] font-medium uppercase tracking-[0.09em] text-muted-foreground">
                Verbindung
              </div>
              <div className="mt-2">
                {status?.connection?.configured ? (
                  <Badge tone={connectionTone(status?.connection?.status)}>
                    {CONNECTION_STATUS_LABEL[status?.connection?.status] ||
                      status?.connection?.status ||
                      "—"}
                  </Badge>
                ) : (
                  <Badge tone="warn">Nicht konfiguriert</Badge>
                )}
              </div>
              {status?.connection?.last_error_category ? (
                <div className="mt-2 text-[11px] text-red-700 dark:text-red-300">
                  Letzter Fehler: {status.connection.last_error_category}
                </div>
              ) : null}
            </div>
            <StatCard label="Kontakte" value={status?.counts?.contacts ?? "—"} />
            <StatCard label="Rechnungen" value={status?.counts?.invoices ?? "—"} />
            <StatCard label="Zahlungen" value={status?.counts?.payments ?? "—"} />
            <StatCard
              label="Letzter erfolgreicher Sync"
              value={
                status?.last_successful_sync
                  ? fmtDateTime(status.last_successful_sync.finished_at)
                  : "—"
              }
              hint={
                status?.last_successful_sync?.entity_scope
                  ? mapLabel(SCOPE_LABEL, status.last_successful_sync.entity_scope)
                  : ""
              }
            />
          </section>

          {/* B1c.3.1b: read-only reconciliation dashboard. Decoupled fetch; its own
              freshness. The existing /status "Letzter erfolgreicher Sync" card above
              and the raw invoice table below are untouched. */}
          <BexioReconciliationSection
            reconciliation={reconciliation}
            loading={reconciliationLoading}
            error={reconciliationError}
            onRetry={loadReconciliation}
          />

          {/* B1c.3.2d: read-only side-by-side (expected selected-month vs. cumulative
              Bexio actuals). Separate section — NOT a delta (SAFETY_HOOKS §9.12).
              Decoupled fetch + own month selector; reconciliation UI untouched. */}
          <BexioExpectedVsInvoicedSection
            data={expVsInv}
            loading={expVsInvLoading}
            error={expVsInvError}
            onRetry={() => loadExpVsInv(expVsInvYear, expVsInvMonth)}
            year={expVsInvYear}
            month={expVsInvMonth}
            onMonthChange={handleExpVsInvMonthChange}
          />

          {/* Slice 1a: read-only, Bexio-write-free invoice-draft preview. Decoupled
              fetch + own month selector. NO Create/Issue/Send/Cancel/Delete action;
              this section never triggers a Bexio write. */}
          <BexioInvoiceDraftPreviewSection
            data={draftPreview}
            loading={draftPreviewLoading}
            error={draftPreviewError}
            onRetry={() => loadDraftPreview(draftPreviewYear, draftPreviewMonth)}
            year={draftPreviewYear}
            month={draftPreviewMonth}
            onMonthChange={handleDraftPreviewMonthChange}
          />

          {/* Sync runs */}
          <Section title="Letzte Sync-Läufe" empty={syncRuns.length === 0}>
            <Table
              head={[
                "Umfang",
                "Status",
                "Start",
                "Ende",
                "Geholt",
                "Gespeichert",
                "Unverändert",
                "Fehler",
              ]}
              rows={syncRuns.map((r) => [
                mapLabel(SCOPE_LABEL, r.entity_scope),
                <Badge key="s" tone={syncStatusTone(r.status)}>
                  {mapLabel(SYNC_STATUS_LABEL, r.status)}
                </Badge>,
                fmtDateTime(r.started_at),
                fmtDateTime(r.finished_at),
                r.fetched_count,
                r.upserted_count,
                r.unchanged_count,
                r.error_category || "—",
              ])}
            />
          </Section>

          {/* Invoices */}
          <Section
            title="Bexio-Rechnungen (schreibgeschützt)"
            empty={
              !isLoadingInvoices && !invoiceLoadError && invoices.length === 0
            }
          >
            <div className="mb-2 flex items-center gap-2 text-[11px] text-muted-foreground">
              {isLoadingInvoices ? (
                <>
                  <span className="h-3 w-3 animate-spin rounded-full border-2 border-input border-t-primary" />
                  {/* Single text node (template literal) so the progress string is
                      matchable as one piece, not split across {…} interpolations. */}
                  <span>{`Lade Rechnungen … ${invoices.length} von ${invoiceTotal} geladen`}</span>
                </>
              ) : (
                <span>{`${invoices.length} von ${invoiceTotal} Rechnungen geladen`}</span>
              )}
            </div>
            {invoiceLoadError ? (
              <div className="mb-2 text-[11px] text-red-700 dark:text-red-300">
                {invoiceLoadError}
              </div>
            ) : null}
            <Table
              head={[
                "Beleg-Nr.",
                "Titel",
                "Kontakt",
                "Status",
                "Betrag",
                "Bezahlt",
                "Offen",
                "Datum",
                "Fällig",
                "Mapping",
                "Aktion",
              ]}
              rows={invoices.map((r) => {
                const im = invoiceMappingStatuses[r.id];
                const mst = im?.mapping_status || "unmapped";
                return [
                  r.document_nr || "—",
                  r.title || "—",
                  r.contact_name || "—",
                  mapLabel(INVOICE_STATUS_LABEL, r.bexio_status),
                  fmtInvoiceMoney(r.total, r.currency),
                  fmtInvoiceMoney(r.total_paid, r.currency, { marker: false }),
                  fmtInvoiceMoney(r.total_remaining, r.currency, { marker: false }),
                  fmtDateCH(r.issue_date),
                  fmtDateCH(r.due_date),
                  <div key="map">
                    <Badge tone={invoiceMappingTone(mst)}>
                      {INVOICE_MAPPING_STATUS_LABEL[mst] || mst}
                    </Badge>
                    {mst === "mapped" && im?.tenancy_reference ? (
                      <div className="mt-1 text-[10px] text-muted-foreground">
                        {im.tenancy_reference}
                        {im.tenant_display_name ? ` · ${im.tenant_display_name}` : ""}
                      </div>
                    ) : null}
                    {admin ? (
                      <button
                        type="button"
                        onClick={() => setMappingInvoice(r)}
                        className="mt-1 block rounded-[8px] border border-input px-3 py-1 text-[11px] text-foreground hover:bg-muted"
                      >
                        {invoiceMappingActionLabel(mst)}
                      </button>
                    ) : (
                      <div className="mt-1 text-[10px] text-muted-foreground">
                        Schreibgeschützt
                      </div>
                    )}
                  </div>,
                  <button
                    key="pos"
                    type="button"
                    onClick={() => setModalInvoice(r)}
                    className="rounded-[8px] border border-input px-3 py-1 text-[11px] text-foreground hover:bg-muted"
                  >
                    Positionen
                  </button>,
                ];
              })}
            />
          </Section>

          {/* Contacts */}
          <Section title="Bexio-Kontakte (schreibgeschützt)" empty={contacts.length === 0}>
            <Table
              head={[
                "Kontakt-Nr.",
                "Name",
                "E-Mail",
                "Typ",
                "Aktualisiert",
                "Zuordnung",
                "Aktion",
              ]}
              rows={contacts.map((r) => {
                const ms = mappingStatuses[r.bexio_contact_id];
                const st = ms?.mapping_status || "unmapped";
                return [
                  r.bexio_contact_id,
                  r.display_name || "—",
                  r.mail || "—",
                  r.contact_type || "—",
                  fmtDateTime(r.synced_at),
                  <div key="z">
                    <Badge tone={mappingTone(st)}>
                      {MAPPING_STATUS_LABEL[st] || st}
                    </Badge>
                    {st === "mapped" && ms?.mapped_tenancy_reference ? (
                      <div className="mt-1 text-[10px] text-muted-foreground">
                        {ms.mapped_tenancy_reference}
                        {ms.mapped_room_name ? ` · ${ms.mapped_room_name}` : ""}
                      </div>
                    ) : null}
                  </div>,
                  admin ? (
                    <button
                      key="a"
                      type="button"
                      onClick={() => setModalContact(r)}
                      className="rounded-[8px] border border-input px-3 py-1 text-[11px] text-foreground hover:bg-muted"
                    >
                      {mappingActionLabel(st)}
                    </button>
                  ) : (
                    <span key="a" className="text-[10px] text-muted-foreground">
                      Schreibgeschützt
                    </span>
                  ),
                ];
              })}
            />
          </Section>
        </>
      )}

      {modalContact ? (
        <BexioContactMappingModal
          contact={modalContact}
          currentStatus={mappingStatuses[modalContact.bexio_contact_id]}
          onClose={() => setModalContact(null)}
          onChanged={handleMappingChanged}
        />
      ) : null}

      {modalInvoice ? (
        <PositionsModal
          invoice={modalInvoice}
          onClose={() => setModalInvoice(null)}
        />
      ) : null}

      {mappingInvoice ? (
        <BexioInvoiceMappingModal
          invoice={mappingInvoice}
          onClose={() => setMappingInvoice(null)}
          onChanged={handleInvoiceMappingChanged}
        />
      ) : null}
    </div>
  );
}

// ─── B1c.2d: invoice mapping status labels + tenancy label helper ────



function invoiceMappingActionLabel(s) {
  if (s === "mapped") return "Ändern";
  if (s === "invalid") return "Reparieren";
  return "Zuordnen";
}

// Human-readable tenancy label: primary tenant name · reference · unit/room (status).
// Derived ONLY from the tenancy list item (no backend change). Never a suggestion.


function Section({ title, empty, children }) {
  return (
    <section className="mb-6">
      <h2 className="mb-2 text-[14px] font-semibold text-foreground">{title}</h2>
      {empty ? (
        <div className="rounded-[10px] border border-border bg-card p-6 text-center text-[12px] text-muted-foreground">
          Keine Daten vorhanden.
        </div>
      ) : (
        children
      )}
    </section>
  );
}

function Table({ head, rows }) {
  return (
    <div className="overflow-x-auto rounded-[10px] border border-border">
      <table className="w-full border-collapse text-[12px]">
        <thead>
          <tr className="bg-card">
            {head.map((h, i) => (
              <th
                key={i}
                className="whitespace-nowrap px-3 py-2 text-left text-[10px] font-medium uppercase tracking-[0.06em] text-muted-foreground"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((cells, ri) => (
            <tr key={ri} className="border-t border-border">
              {cells.map((c, ci) => (
                <td key={ci} className="whitespace-nowrap px-3 py-2 text-foreground">
                  {c}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}


// ─── B1c.2b: read-only Bexio invoice positions modal ────────────────

// Quantity formatter (de-CH, 2 decimals); "amount" is a line quantity, NOT money.
function fmtQuantity(v) {
  if (v == null || v === "") return null;
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return n.toLocaleString("de-CH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

// A parsed hint value or the neutral "nicht erkannt" fallback. A missing hint
// means extraction found nothing — NOT that the room/unit does not exist.
function HintRow({ label, value }) {
  return (
    <div className="text-[11px] text-muted-foreground">
      {label}: <span className="text-foreground">{value || "nicht erkannt"}</span>
    </div>
  );
}

function PositionsModal({ invoice, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(() => {
    let alive = true;
    setLoading(true);
    setError("");
    // Use the LOCAL BexioInvoice.id (never the external bexio_invoice_id).
    fetchBexioInvoicePositions(invoice.id)
      .then((res) => {
        if (alive) setData(res || null);
      })
      .catch((e) => {
        if (alive) setError(e?.message || "Positionen konnten nicht geladen werden.");
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [invoice.id]);

  useEffect(() => {
    const cancel = load();
    return cancel;
  }, [load]);

  // Always sort by position_index locally, even though the backend orders too.
  const positions = [...(data?.positions || [])].sort(
    (a, b) => (a.position_index ?? 0) - (b.position_index ?? 0)
  );
  const month = data?.month_from_invoice_title;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="max-h-[85vh] w-full max-w-[820px] overflow-y-auto rounded-[12px] border border-border bg-card p-5">
        <div className="mb-3 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-[16px] font-bold text-foreground">Bexio-Positionen</h2>
            <div className="mt-1 text-[12px] text-foreground">
              {invoice.document_nr || "—"}
              {invoice.title ? ` · ${invoice.title}` : ""}
            </div>
            <div className="text-[11px] text-muted-foreground">
              Bexio-ID: {data?.bexio_invoice_id ?? invoice.bexio_invoice_id}
            </div>
            {month ? (
              <div className="mt-1 text-[11px] text-muted-foreground">
                {`Monat aus Rechnungstitel: ${month}`}
              </div>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
            aria-label="Schließen"
          >
            ✕
          </button>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 py-10 text-[13px] text-muted-foreground">
            <span className="h-4 w-4 animate-spin rounded-full border-2 border-input border-t-primary" />
            Positionen werden geladen …
          </div>
        ) : error ? (
          <div className="rounded-[10px] border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/10 p-4 text-[13px] text-red-700 dark:text-red-300">
            <div>Positionen konnten nicht geladen werden.</div>
            <button
              type="button"
              onClick={load}
              className="mt-2 rounded-[8px] border border-red-200 dark:border-red-500/30 px-3 py-1 text-[12px] text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-500/20"
            >
              Erneut versuchen
            </button>
          </div>
        ) : positions.length === 0 ? (
          <div className="rounded-[10px] border border-border bg-card p-6 text-center text-[12px] text-muted-foreground">
            Keine Positionsdaten gespiegelt.
          </div>
        ) : (
          <div className="space-y-2">
            {positions.map((p) => {
              const qty = fmtQuantity(p.amount);
              return (
                <div
                  key={p.position_index}
                  className="rounded-[10px] border border-border bg-card p-4"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="text-[13px] font-medium text-foreground">
                      {p.text_plain || "Keine Beschreibung"}
                    </div>
                    {p.position_total != null ? (
                      <div className="shrink-0 text-[13px] font-semibold text-foreground">
                        {fmtInvoiceMoney(p.position_total, invoice.currency)}
                      </div>
                    ) : null}
                  </div>

                  {qty != null ? (
                    <div className="mt-1 text-[11px] text-muted-foreground">
                      Menge: {qty}
                      {p.bexio_unit_name ? ` ${p.bexio_unit_name}` : ""}
                    </div>
                  ) : null}

                  <div className="mt-2 grid grid-cols-1 gap-1 sm:grid-cols-3">
                    <HintRow label="Monat" value={p.extracted_month_label} />
                    <HintRow label="Unit-Code" value={p.extracted_unit_code} />
                    <HintRow label="Zimmer" value={p.extracted_room_label} />
                  </div>

                  <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[10px] text-muted-foreground">
                    <span>Position: {p.position_index}</span>
                    {p.article_id != null ? (
                      <span>Bexio Artikel-ID: {p.article_id} (nur Provenance)</span>
                    ) : null}
                    {p.bexio_unit_name ? (
                      <span>Bexio Einheit: {p.bexio_unit_name} (nicht Vantio Unit)</span>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-4 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="rounded-[8px] border border-input px-3 py-1.5 text-[12px] text-foreground hover:bg-muted"
          >
            Schließen
          </button>
        </div>
      </div>
    </div>
  );
}
